import { Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class KeyboardService {
  #isVisible = false;
  private willShow$ = new Subject<KeyboardInfo>();
  private didShow$ = new Subject<KeyboardInfo>();
  private willHide$ = new Subject<void>();
  private didHide$ = new Subject<void>();

  constructor(private readonly ngZone: NgZone) {}

  #isKeyboardPluginAvailable(): boolean {
    try {
      return Capacitor.isPluginAvailable('Keyboard');
    } catch {
      return false;
    }
  }

  show(): Promise<void> {
    if (!this.#isKeyboardPluginAvailable()) {
      return Promise.resolve();
    }

    return Keyboard.show();
  }

  hide(): Promise<void> {
    if (!this.#isKeyboardPluginAvailable()) {
      return Promise.resolve();
    }

    return Keyboard.hide();
  }

  isVisible(): boolean {
    return this.#isVisible;
  }

  willShow(): Observable<KeyboardInfo> {
    return this.willShow$.asObservable();
  }

  didShow(): Observable<KeyboardInfo> {
    return this.didShow$.asObservable();
  }

  willHide(): Observable<void> {
    return this.willHide$.asObservable();
  }

  didHide(): Observable<void> {
    return this.didHide$.asObservable();
  }

  async addListeners(): Promise<void> {
    if (!this.#isKeyboardPluginAvailable()) {
      return;
    }

    await Keyboard.addListener('keyboardWillShow', (info) => {
      this.ngZone.run(() => {
        this.willShow$.next(info);
      });
    });

    await Keyboard.addListener('keyboardDidShow', (info) => {
      this.ngZone.run(() => {
        this.#isVisible = true;
        this.didShow$.next(info);
      });
    });

    await Keyboard.addListener('keyboardWillHide', () => {
      this.ngZone.run(() => {
        this.willHide$.next();
      });
    });

    await Keyboard.addListener('keyboardDidHide', () => {
      this.ngZone.run(() => {
        this.#isVisible = false;
        this.didHide$.next();
      });
    });
  }

  removeAllListeners(): Promise<void> {
    if (!this.#isKeyboardPluginAvailable()) {
      return Promise.resolve();
    }

    return Keyboard.removeAllListeners();
  }
}
